import { interpolateMagma } from 'd3';

type ColorRangeInfo = {
  colorStart: number;
  colorEnd: number;
  useEndAsStart: boolean;
};

function calculatePoint(i: number, intervalSize: number, colorRangeInfo: ColorRangeInfo): number {
  const { colorStart, colorEnd, useEndAsStart } = colorRangeInfo;
  return useEndAsStart ? colorEnd - i * intervalSize : colorStart + i * intervalSize;
}

/* Must use an interpolated color scale, which has a range of [0, 1] */
export function interpolateColors(
  dataLength = 0,
  colorRangeInfo: ColorRangeInfo = {
    colorStart: 0.02,
    colorEnd: 0.98,
    useEndAsStart: true,
  },
): string[] {
  const colorScale = interpolateMagma;
  const { colorStart, colorEnd } = colorRangeInfo;
  const colorRange = colorEnd - colorStart;
  const intervalSize = colorRange / dataLength;
  let i;
  let colorPoint;
  const colorArray: string[] = [];

  for (i = 0; i < dataLength; i += 1) {
    colorPoint = calculatePoint(i, intervalSize, colorRangeInfo);
    colorArray.push(colorScale(colorPoint));
  }

  return colorArray;
}

export function hex2rgba(hex: string, alpha = 1): string {
  const h = hex.trim().replace(/^#/, '');
  const short = h.length === 3 || h.length === 4;
  const full = short
    ? h
        .split('')
        .map((c) => c + c)
        .join('')
    : h;

  if (!/^[\da-fA-F]{6}([\da-fA-F]{2})?$/.test(full)) {
    throw new Error(`Invalid hex color: ${hex}`);
  }

  const r = parseInt(full.slice(0, 2), 16);
  const g = parseInt(full.slice(2, 4), 16);
  const b = parseInt(full.slice(4, 6), 16);

  // If 8 digits, use embedded alpha; otherwise use the parameter
  const a = full.length === 8 ? parseInt(full.slice(6, 8), 16) / 255 : alpha;

  // clamp alpha to [0,1] and round to 3 decimals for nice strings
  const aClamped = Math.max(0, Math.min(1, a));
  const aRounded = Math.round(aClamped * 1000) / 1000;

  return `rgba(${r}, ${g}, ${b}, ${aRounded})`;
}

export default {
  interpolateColors,
  hex2rgba,
};
